import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import TableWithPagination from "@components/Tables/TableWithPagination";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { AdjustIcon } from "@components/Icons";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { getEffectiveQuickSearchValue } from "@utils/searchHelpers";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { getDefaultFlagOptions } from "@constants/licensePolicy";

const SelectLicensePolicyExceptionRuleModal = ({
  isOpen,
  onClose,
  onAdd = [],
  "data-testid": dataTestId = "select-license-policy-exception-rule-modal",
}) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Table/search state
  const [search, setSearch] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [items, setItems] = useState([]); // license attributes
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedLicensePolicyRules, setSelectedLicensePolicyRules] = useState([]); // selected item objects for return (cross-page)
  const [selectedRowIds, setSelectedRowIds] = useState([]); // array of licenseAttributeSeq for current page display
  const debounceTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { creatorList, fetchSearchInfo } = useSearchInfo();

  const defaultFlagOptions = getDefaultFlagOptions(t);

  const advancedSearchFields = [
    {
      name: "licenseName",
      label: t("license-policy.license-name"),
      type: "text",
      placeholder: t("license-policy.license-name"),
    },
    {
      name: "defaultFlag",
      label: t("license-policy.default-flag"),
      type: "select",
      placeholder: t("license-policy.default-flag"),
      options: defaultFlagOptions,
    },
  ];

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    onPanelClose: () => {
      setSearchFilters({});
      setCurrentPage(1);
    },
  });

  const rowKey = "licenseAttributeSeq";

  const fetchList = (page, searchValue, filters) => {
    setLoading(true);
    const startRow = (page - 1) * 10;
    const licenseIdentifier = getEffectiveQuickSearchValue(searchValue, {
      isAdvancedPanelOpen: showAdvancedSearch,
    });
    getListLicenseAttributes.mutate({
      startRow,
      pageSize: 10,
      ...filters,
      ...(licenseIdentifier ? { licenseIdentifier } : {}),
    });
  };

  // Fetch list API
  const getListLicenseAttributes = useMutationData(
    queryKeys.getListLicenseAttributes,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const list = response.object || [];
          const count = response.objectCount || 0;
          setItems(list);
          setTotalItems(count);
          setTotalPages(Math.ceil(count / 10));
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    },
  );

  // Initialize when open (reset state; fetch runs in the other effect after debouncedSearch is cleared)
  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setDebouncedSearch("");
    setSearchFilters({});
    setShowAdvancedSearch(false);
    setSelectedLicensePolicyRules([]);
    setSelectedRowIds([]);
    setCurrentPage(1);
    fetchSearchInfo();
    // eslint-disable-next-line
  }, [isOpen]);

  // Debounce only the license-identifier search input; on apply reset to page 1
  useEffect(() => {
    if (!isOpen) return;
    setIsSearching(true);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
      setIsSearching(false);
    }, 600);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line
  }, [search]);

  // Fetch when modal opens or page / search / filters change (no debounce on page change)
  useEffect(() => {
    if (!isOpen) return;
    fetchList(currentPage, debouncedSearch, searchFilters);
    // eslint-disable-next-line
  }, [isOpen, currentPage, debouncedSearch, searchFilters, showAdvancedSearch]);

  // Sync selectedRowIds with selectedLicensePolicyRules for current page display
  useEffect(() => {
    setSelectedRowIds(selectedLicensePolicyRules.map(file => file[rowKey]));
  }, [selectedLicensePolicyRules, rowKey]);

  const handleAdvancedSearch = (fields) => {
    setSearchFilters(fields);
    setCurrentPage(1);
    // fetchList will be called automatically by the useEffect
  };

  const handleCheckboxChange = (checkedIdsOnCurrentPage) => {
    // Get items that are checked on current page
    const itemsCheckedOnCurrentPage = items.filter(item => {
      return checkedIdsOnCurrentPage.includes(item[rowKey]);
    });

    // Get items that are unchecked on current page
    const itemsUncheckedOnCurrentPage = items.filter(item => {
      return !checkedIdsOnCurrentPage.includes(item[rowKey]);
    });

    // Remove items that were unchecked on current page from global selection
    const globalSelectionAfterRemoving = selectedLicensePolicyRules.filter(globalSelectedItem => {
      return !itemsUncheckedOnCurrentPage.some(uncheckedItem =>
        uncheckedItem[rowKey] === globalSelectedItem[rowKey]
      );
    });

    // Add newly checked items that aren't already in global selection
    const newlyCheckedItems = itemsCheckedOnCurrentPage.filter(checkedItem => {
      return !globalSelectionAfterRemoving.some(globalItem =>
        globalItem[rowKey] === checkedItem[rowKey]
      );
    });

    setSelectedLicensePolicyRules([...globalSelectionAfterRemoving, ...newlyCheckedItems]);
  };

  const handleRowClick = (row) => {
    const isCurrentlySelected = selectedLicensePolicyRules.some(file =>
      file[rowKey] === row[rowKey]
    );

    if (isCurrentlySelected) {
      // Remove from selection
      setSelectedLicensePolicyRules(selectedLicensePolicyRules.filter(file =>
        file[rowKey] !== row[rowKey]
      ));
    } else {
      // Add to selection
      setSelectedLicensePolicyRules([...selectedLicensePolicyRules, row]);
    }
  };

  const handleAdd = () => {
    onAdd(selectedLicensePolicyRules);
    setSelectedLicensePolicyRules([]);
  };

  // Columns
  const columns = ["licenseIdentifier", "licenseName", "defaultFlag"];
  const columnConfig = {
    licenseIdentifier: { title: t("license-policy.license-identifier"), style: { textAlign: "left" } },
    licenseName: { title: t("license-policy.license-name"), style: { textAlign: "left" } },
    defaultFlag: {
      title: t("license-policy.default-flag"),
      style: { textAlign: "left", width: "15%" },
      customRender: (value) => <span className={`text-sm ${colors.textPrimary}`}>{value}</span>,
    },
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[90vw] max-w-[900px] min-w-[900px] max-h-[85vh] overflow-auto rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("license-policy.select-license")}</h2>
          </div>
        </div>

        {/* Search */}
        <div className="space-y-4 px-6 pb-4" data-testid={`${dataTestId}-content`}>
          <div className="flex items-center gap-4 max-w-xl">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 ${colors.textSecondary}`} />
              <input
                type="text"
                placeholder={t("license-policy.search-by-license-identifier")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("project.advancedSearch")}</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 mb-4">
            <AdvancedSearch
              title={t("project.advancedSearch")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
            />
          </div>
        )}

        {/* Table */}
        <div className="mb-4 px-6">
          <TableWithPagination
            dataTestId={`${dataTestId}-table`}
            columns={columns}
            columnConfig={columnConfig}
            data={items}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
            rowKey={rowKey}
            totalPages={totalPages}
            totalData={totalItems}
            maxRowsPerPage={10}
            hasCheckbox={true}
            selectedRows={selectedRowIds}
            onCheckboxChange={handleCheckboxChange}
            onRowClick={handleRowClick}
            showPagination={true}
          />
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleAdd} disabled={selectedLicensePolicyRules.length === 0}>
                {t("common.add-button")}
                {` (${selectedLicensePolicyRules.length})`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectLicensePolicyExceptionRuleModal; 